import { Injectable, Injector } from '@angular/core';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { NavController } from '@ionic/angular';
import { firstValueFrom, of, timeout } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { PromptService } from '../promtp-service/prompt.service';
import { Browser } from '@capacitor/browser';
import { environment } from 'src/environments/environment';
import { TranslatorService } from '../translator-service/translator.service';
import { EnvironmentConfigService } from '../environment-config-service/environment-config-service';
import { Events } from '../events/events';
import { SecureStorageService } from '../secure-storage/secure-storage-service';
import { IPersistedUserInfo, UserInfoService } from './user-info-service';
import { BiometricLoginDetail, UserData } from './models/user-data.model';
import { ResetPasswordRequestModel } from 'src/app/api/proxy/authentication/models/reset-password-request-model';
import { ChangePasswordRequestModel } from 'src/app/api/proxy/authentication/models/change-password-request-model';
import { VerifyOtpRequestModel } from 'src/app/api/proxy/authentication/models/verify-otp-request-model';
import { Semaphore } from 'src/app/utilities/semaphore.utility';
import { RefreshTokenResponseModel } from 'src/app/api/proxy/authentication/models/refresh-token-response-model';
import { UserAccessTokenModel } from './models/user-access-token-model';
import { PractitionerService } from '../practitioner-service/practitioner.service';
import { AuthenticationEntraExternalDService } from 'src/app/api/proxy/authentication/services/authentication-entra-external-d.service';
import {
  ActionType,
  EntraExternalIdAccessTokenResponse,
  EntraExternalIdLoginRequestModel,
  GetLoginClinicGroupsResponseModel,
} from 'src/app/api/proxy/authentication/authentication-models';
import { v4 as uuidv4 } from 'uuid';
import { NetworkService } from '../network-service/network-service';
import { BLEManagerService } from '../ble/ble-manager.service';
import { IQConnect } from '../ble/IQConnect';

/**
 * True when the stored access token can no longer be sent as a bearer.
 * Missing user, missing token, or `exp` already reached all count as expired.
 * This is stricter than the refresh skew: reopen must not keep a dead bearer.
 */
export function isStoredAccessTokenExpired(
  user: { access_token?: string | null; tokenExpiry?: string | null } | null | undefined,
  nowMs: number,
): boolean {
  if (!user?.access_token || !user.tokenExpiry) {
    return true;
  }

  const exp = new Date(user.tokenExpiry).getTime();
  return Number.isNaN(exp) || nowMs >= exp;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private NETWORK_TIMEOUT = 40000;
  private REFRESH_TIMEOUT = 15000;
  private tokenRefreshRetry = 0;
  private readonly MAX_REFRESH_RETRIES = 6;
  private readonly RETRY_BACKOFF_BASE = 1000; // in milliseconds
  private readonly RETRY_BACKOFF_MAX = 60000;
  private readonly DEFAULT_RT_TTL_SEC = 90 * 24 * 60 * 60; // 90 days in seconds
  private bridgeTokenId: string | null = null;

  constructor(
    public navCtrl: NavController,
    private _EnvironmentConfigService: EnvironmentConfigService,
    private _localAccountService: AuthenticationEntraExternalDService,
    private _PromptService: PromptService,
    private _PractitionerService: PractitionerService,
    private _networkService: NetworkService,
    private injector: Injector,
  ) {
    Events.appPaused.subscribe(async () => {
      this.clearRefresh();
    });
  }

  public async init(): Promise<void> {
    await this.setupRefreshTimer();
  }

  get BridgeTokenId(): string {
    if (this.bridgeTokenId == null) {
      this.bridgeTokenId = uuidv4();
    }

    return this.bridgeTokenId;
  }

  public async tokenExpired(): Promise<boolean> {
    const user = await UserInfoService.getUserInfo();
    if (!user?.tokenExpiry) return true;
    const exp = new Date(user.tokenExpiry).getTime();
    const now = Date.now();
    const SKEW_MS = 60_000; // 60s skew
    return now >= exp - SKEW_MS;
  }

  /** Access-token TTL has elapsed. Used when restoring a closed browser session. */
  public async accessTokenExpired(): Promise<boolean> {
    const user = await UserInfoService.getUserInfo();
    return isStoredAccessTokenExpired(user, Date.now());
  }

  public async token(): Promise<string | null> {
    const userInfo = await UserInfoService.getUserInfo();
    if (userInfo && userInfo.access_token) {
      return userInfo.access_token;
    }
    return null;
  }

  public async saveBiometricLogin(countryCode: string, email: string, password: string) {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    const biometricLogin: BiometricLoginDetail = {
      username: email,
      password: password,
      countrycode: countryCode,
    };
    await SecureStorageService.save(SecureStorageKey.BiometricLogin, biometricLogin);
  }

  public async getBiometricLogin(): Promise<BiometricLoginDetail | null> {
    if (Capacitor.getPlatform() === 'web') {
      return null;
    }

    const userData = await SecureStorageService.getById<BiometricLoginDetail>(
      SecureStorageKey.BiometricLogin,
    );

    if (userData == null) {
      return null;
    }
    const userInfo = userData;
    if (!userInfo) {
      return null;
    }

    return userInfo;
  }

  public async clearBiometricLogin(): Promise<void> {
    const bioData = await this.getBiometricLogin();
    if (bioData) {
      await SecureStorageService.save(SecureStorageKey.BiometricLogin, '');
      await SecureStorageService.remove(SecureStorageKey.BiometricLogin);
    }
  }

  public async getLoginClinicGroups(
    emailAddress: string,
  ): Promise<GetLoginClinicGroupsResponseModel | null> {
    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration();
    if (!envConfig) {
      return null;
    }

    try {
      return await firstValueFrom(
        this._localAccountService
          .entraExternalDGetLoginClinicGroupsPost({ body: { emailAddress } })
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'getLoginClinicGroups');
      return null;
    }
  }

  public async requestOtpLogin(
    emailAddress: string,
    pasw: string,
    clinicGroupId?: string,
  ): Promise<loginOtpResult> {
    let result: loginOtpResult = {
      success: false,
      actionType: ActionType.None,
      errorMessage: 'Unable to login',
    };

    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration();
    if (!envConfig) {
      result.errorMessage = 'Environment configuration is missing';
      return result;
    }

    const loginRequest: EntraExternalIdLoginRequestModel = {
      clientId: envConfig.authenticateOptions!.appId!,
      scope: envConfig.authenticateOptions!.scope!,
      userName: emailAddress,
      password: pasw,
      clinicGroupId: clinicGroupId ?? null,
    };

    let response: EntraExternalIdAccessTokenResponse | null;

    try {
      response = await firstValueFrom(
        this._localAccountService
          .entraExternalDCreateOtpFromLoginPost({ body: loginRequest })
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
    } catch (error) {
      const credentialsMessage = this.credentialsMessageFromHttpError(error);
      if (credentialsMessage) {
        result.errorMessage = credentialsMessage;
        return result;
      }
      await this._PromptService.showNetworkConnectionError(error, 'login');
      // Network popup already shown — avoid a second alert in the login page.
      result.errorMessage = '';
      return result;
    }

    if (response) {
      if (response.error) {
        result.errorMessage = this.loginFailureMessage(
          response.error,
          response.error_description,
        );
        return result;
      } else {
        result.actionType = response.action || ActionType.None;
        result.success = true;
        return result;
      }
    }

    return result;
  }

  public async loginUserWithOtp(
    emailAddress: string,
    password: string,
    otp: number,
    clinicGroupId?: string | null,
  ): Promise<boolean> {
    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration();
    if (!envConfig) {
      await this._PromptService.showError('Environment configuration is missing');
      return false;
    }

    const loginRequest: EntraExternalIdLoginRequestModel = {
      clientId: envConfig.authenticateOptions!.appId!,
      scope: envConfig.authenticateOptions!.scope!,
      userName: emailAddress,
      password: password,
      otp: otp,
      clinicGroupId: clinicGroupId ?? null,
    };

    let response: EntraExternalIdAccessTokenResponse | null;

    try {
      response = await firstValueFrom(
        this._localAccountService
          .entraExternalDLoginUserPost({ body: loginRequest })
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
    } catch (error) {
      const credentialsMessage = this.credentialsMessageFromHttpError(error);
      if (credentialsMessage) {
        await this._PromptService.showError(credentialsMessage);
        return false;
      }
      await this._PromptService.showNetworkConnectionError(error, 'loginUserWithOtp');
      return false;
    }

    if (response) {
      if (response.error) {
        await this._PromptService.showError(
          this.loginFailureMessage(response.error, response.error_description),
        );
        return false;
      } else {
        if (clinicGroupId) {
          await UserInfoService.setClinicGroupForEmail(emailAddress, clinicGroupId);
        }
        const tokenResponse: UserAccessTokenModel = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          emailVerified: response.emailVerified,
          error: response.error,
          error_description: response.error_description,
        };
        return await this.handleTokenResponse(tokenResponse, emailAddress, clinicGroupId);
      }
    }

    return false;
  }

  public async handleTokenResponse(
    response: UserAccessTokenModel,
    emailAddress?: string,
    clinicGroupId?: string | null,
  ): Promise<boolean> {
    if (response && response.access_token && response.access_token.length > 0 && !response.error) {
      const tokenResponse = response as EntraExternalIdAccessTokenResponse;
      const token = this.parseJwt(tokenResponse.access_token!);
      // Convert `exp` to milliseconds and create a Date object for expiry
      const tokenExpiryUtc = new Date(token.exp * 1000).toISOString();

      let rtTtlSec = 0; //tokenResponse.refresh_token_expires_in;
      if (!rtTtlSec || rtTtlSec <= 0) {
        rtTtlSec = this.DEFAULT_RT_TTL_SEC;
      }

      const oldUser = await UserInfoService.getUserInfo();
      const resolvedEmail = UserInfoService.normalizeEmailKey(
        emailAddress ?? token.emailAddress ?? token.userEmailAddress ?? '',
      );
      const resolvedClinicGroupId =
        clinicGroupId ??
        (resolvedEmail ? await UserInfoService.getClinicGroupForEmail(resolvedEmail) : null);
      const nowMs = Date.now();
      const refreshTokenExpiryIso =
        rtTtlSec != null
          ? new Date(nowMs + rtTtlSec * 1000).toISOString()
          : (oldUser?.refreshTokenExpiry ?? null);

      const userInfo: UserData = {
        sub: token.oid,
        practitionerId: token.practitionerId ?? oldUser?.practitionerId,
        given_name: token.given_name ?? oldUser?.given_name,
        family_name: token.family_name ?? oldUser?.family_name,
        name: token.name ?? oldUser?.name,
        middleInitial: oldUser?.middleInitial,
        suffix: oldUser?.suffix,
        credentials: oldUser?.credentials,
        profile_picture: oldUser?.profile_picture,
        access_token: tokenResponse.access_token ?? oldUser?.access_token ?? undefined,
        refresh_token: tokenResponse.refresh_token ?? oldUser?.refresh_token ?? undefined,
        refreshTokenExpiry: refreshTokenExpiryIso,
        tokenExpiry: tokenExpiryUtc,
        tokenClaims: token,
        email: resolvedEmail || token.emailAddress || token.userEmailAddress,
        language: token.language ?? oldUser?.language,
        emailVerified: tokenResponse.emailVerified,
        localAccount: token.localAccount,
        socialIssuer: token.idp,
        b2cFlow: token.tfp,
        clinicGroupId: resolvedClinicGroupId,
        permissions: this.getPermissionsFromToken(token),
      };

      if (oldUser && oldUser.sub !== userInfo.sub) {
        await SecureStorageService.remove(SecureStorageKey.CurrentUser);
      }
      await UserInfoService.setUserInfo(userInfo);
      await this._PractitionerService.setFacilities(true);
      await this._PractitionerService.setPractitionerInfo();
      const loggedInUser = await this.setUserLoggedInAsync();
      if (loggedInUser)
        await SecureStorageService.saveWithPrefix<IPersistedUserInfo>(
          userInfo.email!,
          {
            userName: loggedInUser.email!,
            firstName: loggedInUser.given_name!,
            lastName: loggedInUser.family_name!,
            photo: loggedInUser.profile_picture,
          },
          SecureStorageService.Prefix.User,
        );
      return true; // Login successful
    }
    return false;
  }

  public async forgotPasswordRequest(email: string): Promise<boolean> {
    try {
      await firstValueFrom(
        this._localAccountService
          .entraExternalDForgotPasswordRequestPost({
            body: {
              emailAddress: email,
            },
          })
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
      return true;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'forgotPasswordRequest');
      return false;
    }
  }

  public async resetForgotPassword(email: string, password: string): Promise<boolean> {
    try {
      const model: ResetPasswordRequestModel = { emailAddress: email, newPassword: password };
      await firstValueFrom(
        this._localAccountService
          .entraExternalDResetForgotPasswordPost({ body: model })
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
      await this.clearBiometricLogin();
      return true;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'resetForgotPassword');
      return false;
    }
  }

  public async changePassword(password: string) {
    try {
      const model: ChangePasswordRequestModel = { newPassword: password };
      await firstValueFrom(
        this._localAccountService
          .entraExternalDChangePasswordPost({ body: model })
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
      if (Capacitor.isNativePlatform()) {
        const bioPassword = await this.getBiometricLogin();
        if (bioPassword) {
          await this.saveBiometricLogin(bioPassword.countrycode, bioPassword.username, password);
        }
      }
      return true;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'changePassword');
      return false;
    }
  }

  public async verifyOTP(model: VerifyOtpRequestModel): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this._localAccountService
          .entraExternalDVerifyOtpAsyncPost({ body: model })
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
      return response == true;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'verifyOTP');
      return false;
    }
  }

  /**
   * @param options.logoutOnFailure When false, do not logout on refresh failure (e.g. pre-logout encounter save).
   */
  public async refreshToken(options?: {
    logoutOnFailure?: boolean;
  }): Promise<void> {
    await this.performTokenRefresh(options);
  }

  public async logDebug(mesg: string) {
    const userInfo = await UserInfoService.getUserInfo();
    if (userInfo && userInfo.email && userInfo.email.toLocaleLowerCase().startsWith('willie')) {
      try {
      } catch {}
    }
  }

  public async logout(reason?: string): Promise<void> {
    if (reason) {
      await this.logDebug('Logout : ' + reason);
    } else {
      await this.logDebug('Logout : no reason');
    }
    await this.disconnectBleDeviceIfConnected();
    await this.clearUserData(false);

    setTimeout(() => {
      const redirectPath = Capacitor.getPlatform() === 'android' ? '/landing' : '/login';
      window.location.href = redirectPath;
    }, 1000);
  }

  /** Best-effort BLE disconnect so a connected stylus is not left paired after logout. */
  private async disconnectBleDeviceIfConnected(): Promise<void> {
    if (!IQConnect.getConnected()) {
      return;
    }

    try {
      const bleService = this.injector.get(BLEManagerService, null);
      if (!bleService) {
        return;
      }
      await bleService.disconnect();
    } catch (error) {
      console.warn('Logout: BLE disconnect failed', error);
    }
  }

  public async clearUserData(clearBiologin: boolean = true): Promise<void> {
    await UserInfoService.setUserInfo(null);
    this.clearRefresh(); //terminate timers

    const region = await SecureStorageService.getById(SecureStorageKey.CurrentRegion);
    const bioLogin = await SecureStorageService.getById(SecureStorageKey.BiometricLogin);
    //await SecureStorageService.clear();
    if (bioLogin && !clearBiologin) {
      await SecureStorageService.save(SecureStorageKey.BiometricLogin, bioLogin);
    }
    if (region) {
      await SecureStorageService.save(SecureStorageKey.CurrentRegion, region);
      await this._EnvironmentConfigService.EnsureNetworkConfiguration();
    }
  }

  private refreshInFlight: Promise<void> | null = null;
  private async performTokenRefresh(options?: {
    logoutOnFailure?: boolean;
  }): Promise<void> {
    // If a refresh is already running, reuse it.
    if (this.refreshInFlight) {
      await this.refreshInFlight;
      return;
    }

    // Start a new refresh, and ensure the guard is cleared when done.
    this.refreshInFlight = this._performTokenRefreshInner(options)
      .catch(async (err) => {
        // propagate but also log if you want
        await this.logDebug(`REFRESH TOKEN unhandled error: ${err?.message ?? err}`);
        throw err;
      })
      .finally(() => {
        this.refreshInFlight = null;
      });

    await this.refreshInFlight;
  }

  private async logoutOnRefreshFailure(
    reason: string,
    logoutOnFailure: boolean,
  ): Promise<void> {
    if (!logoutOnFailure) {
      await this.logDebug(`REFRESH TOKEN failure suppressed (no logout): ${reason}`);
      return;
    }
    await this.logout(reason);
  }

  private async _performTokenRefreshInner(options?: {
    logoutOnFailure?: boolean;
  }): Promise<void> {
    const logoutOnFailure = options?.logoutOnFailure !== false;

    try {
      const userInfo = await UserInfoService.getUserInfo();

      if (!userInfo || !userInfo.access_token || !userInfo.refresh_token)
        //we never had a logged in user go empty
        return;

      if (!userInfo?.refresh_token) {
        await this.logoutOnRefreshFailure('No refresh token available', logoutOnFailure);
        return;
      }

      const expired = await this.tokenExpired();
      if (!expired) return;

      if (userInfo && userInfo.refreshTokenExpiry && userInfo.refreshTokenExpiry.length > 0) {
        if (
          userInfo?.refreshTokenExpiry &&
          Date.now() >= new Date(userInfo.refreshTokenExpiry).getTime() - 60_000
        ) {
          await this.logoutOnRefreshFailure('Refresh token expired', logoutOnFailure);
          return;
        }
      }

      //If user is offline, perform logout on token expiry (unless suppressed)
      if (!this._networkService.isOnline) {
        await this.logoutOnRefreshFailure('Refresh token expired', logoutOnFailure);
        return;
      }

      if (userInfo) {
        const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfigurationForToken();
        if (!envConfig) {
          return;
        }

        let response: RefreshTokenResponseModel | null = null;
        this.tokenRefreshRetry++;

        response = await firstValueFrom(
          this._localAccountService
            .entraExternalDRefreshTokenPost({
              body: {
                applicationId: envConfig!.authenticateOptions!.appId!,
                refreshToken: userInfo.refresh_token!,
                scope: envConfig.authenticateOptions!.scope!,
              },
            })
            .pipe(timeout(this.REFRESH_TIMEOUT)),
        );

        if (!response?.access_token || response?.error) {
          await this.logDebug(
            `REFRESH TOKEN failed - invalid 200 body: ${response?.error ?? 'missing access_token'}`,
          );
          if (this.tokenRefreshRetry > 1) {
            // or 0 if you prefer
            await this.logoutOnRefreshFailure('Refresh failed: invalid response', logoutOnFailure);
            this.tokenRefreshRetry = 0;
          } else {
            return this.scheduleRefreshRetry();
          }
          return;
        }

        await this.handleTokenResponse({
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          emailVerified: response.emailVerified,
          error: response.error,
          error_description: response.error_description,
        });
      }
    } catch (err: any) {
      // Angular HttpErrorResponse shape
      const httpStatus = err?.status as number | undefined;
      const body = err?.error ?? {};
      const code = (body?.error ?? '').toString();
      const desc = (
        body?.error_description ??
        body?.message ??
        err?.message ??
        'Unknown error'
      ).toString();

      // Helpers
      const isInvalidGrant = (httpStatus === 400 || httpStatus === 401) && code === 'invalid_grant';
      const isAccountDisabled = httpStatus === 403 && code === 'account_disabled';
      const isUserNotFound = httpStatus === 404 && code === 'user_not_found';
      const isOrgMismatch = httpStatus === 409 && code === 'org_mismatch';
      const isUpstream = httpStatus === 502 || httpStatus === 503; // transient

      if (isOrgMismatch) {
        // ✅ show message BEFORE logout
        await this._PromptService.showError('Your organisation has changed. Please sign in again.');
        await this.logDebug(`REFRESH TOKEN failed: ORG_MISMATCH → logout`);
        await this.logoutOnRefreshFailure('Organisation changed', logoutOnFailure);
        this.tokenRefreshRetry = 0;
        return;
      }

      if (isAccountDisabled) {
        await this._PromptService.showError('Your account is disabled. Please contact support.');
        await this.logoutOnRefreshFailure('Account disabled', logoutOnFailure);
        this.tokenRefreshRetry = 0;
        return;
      }

      if (isUserNotFound) {
        await this._PromptService.showError(
          'We could not find your account. Please sign in again.',
        );
        await this.logoutOnRefreshFailure('User not found', logoutOnFailure);
        this.tokenRefreshRetry = 0;
        return;
      }

      if (isInvalidGrant) {
        await this.logDebug(`REFRESH TOKEN failed: INVALID_GRANT → logout`);
        await this.logoutOnRefreshFailure('Refresh failed: invalid_grant', logoutOnFailure);
        this.tokenRefreshRetry = 0;
        return;
      }

      if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
        await this.logDebug(`REFRESH TOKEN failed (HTTP ${httpStatus}, ${code}): ${desc}`);
        await this.logoutOnRefreshFailure(
          `Refresh failed: ${code || httpStatus}`,
          logoutOnFailure,
        );
        this.tokenRefreshRetry = 0;
        return;
      }

      // Transient / unknown errors → retry (bounded)
      const message = desc;
      if (isUpstream || !httpStatus || httpStatus >= 500) {
        if (this.tokenRefreshRetry > this.MAX_REFRESH_RETRIES) {
          await this._PromptService.showNetworkConnectionError(err, 'refresh');
          await this.logoutOnRefreshFailure(
            `Refresh failed after ${this.tokenRefreshRetry} retries: ${message}`,
            logoutOnFailure,
          );
          this.tokenRefreshRetry = 0;
        } else {
          await this.logDebug(
            `REFRESH TOKEN retry scheduled: attempt ${this.tokenRefreshRetry}, error: ${message}`,
          );
          return this.scheduleRefreshRetry();
        }
        return;
      }
    }
  }

  private scheduleRefreshRetry(): void {
    const delay = Math.min(
      this.RETRY_BACKOFF_BASE * Math.pow(2, this.tokenRefreshRetry),
      this.RETRY_BACKOFF_MAX,
    );

    this.clearRefresh(); // Prevent multiple retry timers

    this.refreshTimout = setTimeout(async () => {
      await this.performTokenRefresh();
    }, delay);

    this.logDebug(`Scheduled refresh retry in ${delay / 1000}s`);
  }

  private async setUserLoggedInAsync(): Promise<UserData | null> {
    const userInfo = await UserInfoService.getUserInfo();
    if (userInfo) {
      await this.setupRefreshTimer();
    }

    return userInfo;
  }

  private refreshTimout: any | null = null;
  private clearRefresh() {
    if (this.refreshTimout) {
      clearTimeout(this.refreshTimout);
      this.refreshTimout = null;
    }
  }

  private async singleRefreshSetup() {
    const user = await UserInfoService.getUserInfo();
    if (!user?.tokenExpiry) return;
    const expMs = new Date(user.tokenExpiry).getTime();
    let delay = Math.max(0, expMs - Date.now() + 2000); // fire ~2s after expiry
    this.clearRefresh();
    this.refreshTimout = setTimeout(async () => {
      await this.performTokenRefresh(); // no "timerRefresh" escape hatch
    }, delay);
  }

  private refreshLock = new Semaphore(1); //must be another refresh timee

  private async setupRefreshTimer() {
    const result = false;
    await this.refreshLock.callFunction(async () => {
      await this.singleRefreshSetup();
    }, []);
    return result;
  }

  public async DeleteProfile(): Promise<boolean> {
    try {
      await firstValueFrom(
        this._localAccountService
          .entraExternalDDeleteAccountDelete()
          .pipe(timeout(this.NETWORK_TIMEOUT)),
      );
      //logout and clear local storage
      await this.clearBiometricLogin();
      await this.logout();
      await SecureStorageService.clear();

      return true;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'DeleteProfile');
      console.error('Unexpected error:', error);
      return false;
    }
  }

  public async externalSigninOrSignup(
    providerName: string,
    isSignup: boolean,
    state: string | undefined = undefined,
  ): Promise<boolean> {
    if (!state) state = 'login-callback';

    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration();
    if (!envConfig || !envConfig.authenticateOptions) {
      return false;
    }

    envConfig.authenticateOptions.externalAuthProviders;

    const socialProvider = envConfig.authenticateOptions.externalAuthProviders?.find(
      (a) => a.providerName === providerName,
    );
    if (!socialProvider) return false;

    const codeVerifier = this.generateRandomString(128);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    let flow: string | undefined | null;

    if (isSignup) flow = socialProvider.signupFlow;
    else flow = socialProvider.loginFlow;

    if (!flow) return false;

    let redirectUrl = environment.deepLinkURLBase;
    if (!redirectUrl.endsWith('/')) redirectUrl = redirectUrl + '/';
    redirectUrl = redirectUrl + 'login-callback';

    if (Capacitor.getPlatform() == 'ios') redirectUrl = environment.iosAuthRedirect;
    else if (Capacitor.getPlatform() == 'android') redirectUrl = environment.androidAuthRedirect;

    const scope = [
      'openid',
      'profile',
      'offline_access',
      envConfig.authenticateOptions?.scope,
    ].join(' ');

    const pkceRequest: PKCERequest = {
      codeVerifier: codeVerifier,
      flow: flow,
      redirectUrl: redirectUrl,
      scope: envConfig.authenticateOptions.scope!,
      clientId: envConfig.authenticateOptions.appId!,
      baseURL: envConfig.authenticateOptions.b2CBaseURL!,
    };

    await SecureStorageService.save(SecureStorageKey.PKCECodeVerifier, pkceRequest);

    const authUrl =
      `${envConfig.authenticateOptions.b2CBaseURL}oauth2/v2.0/authorize` +
      `?p=${flow}` +
      `&client_id=${envConfig.authenticateOptions?.appId}` +
      `&redirect_uri=${encodeURIComponent(redirectUrl)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scope)}` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256` +
      `&state=${encodeURIComponent(state)}` +
      `&prompt=login`;

    if (Capacitor.isNativePlatform()) {
      await Browser.open({
        url: authUrl,
      });
    } else {
      await Browser.open({
        url: authUrl,
        windowName: '_self',
      });
    }
    return true;
  }

  public async processSocialLoginCode(
    code: string | null | undefined,
    error: string | null | undefined,
    errorDescription: string | null | undefined,
    state: string | null | undefined,
    translator: TranslatorService,
  ): Promise<ExternalSignUpCallbackResult> {
    let result: ExternalSignUpCallbackResult = {
      success: false,
      errorMessage: translator.translateText('account-service.errors.generic-error'),
      redirectURL: 'home',
    };

    if (state) {
      if (state.startsWith('LINKACCOUNT')) {
        result.redirectURL = 'home/link-account';
      }
      if (state.startsWith('SIGNUP-CONSUMER')) {
        result.redirectURL = 'v2/registration/get-vivascore';
      }
    }

    if (error) {
      const decodedError = decodeURIComponent(errorDescription || '');
      let errorMessage = decodedError;
      if (decodedError.includes('AADB2C99001')) {
        errorMessage = translator.translateText('account-service.errors.account-already-inuse');
      }

      if (decodedError.includes('AADB2C90075')) {
        errorMessage = translator.translateText('account-service.errors.social-provider-failed');
      }

      if (decodedError.includes('AADB2C99002')) {
        errorMessage = translator.translateText('account-service.errors.user-does-not-exist');
      }

      result.errorMessage = errorMessage;
      result.success = false;
      return result;
    }

    if (code) {
      const storedValue = await SecureStorageService.getById<PKCERequest>(
        SecureStorageKey.PKCECodeVerifier,
      );
      if (!storedValue) {
        return result;
      }

      const requestType: PKCERequest = storedValue;
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: requestType.clientId,
        redirect_uri: requestType.redirectUrl,
        code: code,
        code_verifier: requestType.codeVerifier!,
        scope: 'openid profile offline_access ' + requestType.scope,
      });

      try {
        const postResponse = await fetch(
          `${requestType.baseURL}oauth2/v2.0/token?p=${requestType.flow}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
          },
        );

        const tokenResult: PKETokenResult = await postResponse.json();

        if (tokenResult && tokenResult.access_token && tokenResult.access_token.length > 0) {
          const token = this.parseJwt(tokenResult.access_token);
          // Convert `exp` to milliseconds and create a Date object for expiry
          const tokenExpiry = new Date(token.exp * 1000);
          // Subtract 2 minutes from the expiry time
          const renewTokenTime = new Date(tokenExpiry.getTime() - 2 * 60 * 1000);
          const userInfo: UserData = {
            sub: token.oid,
            given_name: token.given_name,
            family_name: token.family_name,
            name: token.name,
            access_token: tokenResult.access_token!,
            refresh_token: tokenResult.refresh_token!,
            tokenExpiry: renewTokenTime.toISOString(),
            tokenClaims: token,
            email: token.userEmailAddress!,
            language: token.language,
            emailVerified: true,
            localAccount: token.localAccount,
            socialIssuer: token.idp,
            b2cFlow: token.tfp,
            permissions: this.getPermissionsFromToken(token),
          };

          const oldUser = await UserInfoService.getUserInfo();
          if (oldUser && oldUser.sub !== userInfo.sub) {
            await SecureStorageService.clear();
          }
          await UserInfoService.setUserInfo(userInfo);
          await this.setUserLoggedInAsync();

          //investigate state variable to see what to do
          if (state) {
            if (state.startsWith('SIGNUP-CONSUMER')) {
              result.success = true;
              result.redirectURL = 'v2/registration/confirm-profile';
              return result;
            }
          }
          result.success = true;
          result.redirectURL = 'home';
        }
      } catch (error) {
        console.error('Token exchange failed:', error);
        result.success = false;
      }
    }
    return result;
  }

  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const values = new Uint32Array(length);
    window.crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }
    return result;
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );
    let payload = JSON.parse(jsonPayload);

    if (!payload.personId) {
      payload.personId = payload.extension_PersonId;
    }

    if (!payload.organizationId) payload.organizationId = payload.extension_OrganizationId;

    if (!payload.language) payload.language = payload.extension_Language;

    if (!payload.userEmailAddress) payload.userEmailAddress = payload.extension_UserEmailAddress;

    if (!payload.systemFunctions && payload.extension_SystemFunctions)
      payload.systemFunctions = payload.extension_SystemFunctions.split('|');

    if (!payload.idp) payload.localAccount = true;
    else payload.localAccount = false;

    return payload;
  }

  private getPermissionsFromToken(token: Record<string, unknown>): string[] {
    const permissions = token['permissions'];

    if (Array.isArray(permissions)) {
      return permissions
        .filter((permission): permission is string => typeof permission === 'string')
        .map((permission) => permission.trim())
        .filter((permission) => permission.length > 0);
    }

    // Entra may emit a single claim value as a string instead of a one-element array.
    if (typeof permissions === 'string') {
      const trimmed = permissions.trim();
      return trimmed.length > 0 ? [trimmed] : [];
    }

    return [];
  }

  private static readonly INVALID_CREDENTIALS_MESSAGE =
    'Username or Password is incorrect. Please try again.';

  /** Friendly login error; maps IdP codes/noise to a normal popup message. */
  private loginFailureMessage(
    errorCode?: string | null,
    errorDescription?: string | null,
  ): string {
    if (this.looksLikeInvalidCredentials(errorCode, errorDescription)) {
      return AuthService.INVALID_CREDENTIALS_MESSAGE;
    }
    const description = (errorDescription ?? '').trim();
    return description || AuthService.INVALID_CREDENTIALS_MESSAGE;
  }

  private looksLikeInvalidCredentials(
    errorCode?: string | null,
    errorDescription?: string | null,
  ): boolean {
    const text = `${errorCode ?? ''} ${errorDescription ?? ''}`.toLowerCase();
    return (
      text.includes('invalid_grant') ||
      text.includes('access_denied') ||
      text.includes('aadsts') ||
      text.includes('invalid credentials') ||
      text.includes('username or password') ||
      text.includes('password is incorrect')
    );
  }

  /** Returns the friendly credentials message when an HTTP error looks like bad login; otherwise null. */
  private credentialsMessageFromHttpError(error: unknown): string | null {
    const body = (error as { error?: Record<string, unknown> } | null)?.error ?? {};
    const code = String(body['error'] ?? body['code'] ?? '');
    const description = String(body['error_description'] ?? body['message'] ?? '');
    if (!this.looksLikeInvalidCredentials(code, description)) {
      return null;
    }
    return AuthService.INVALID_CREDENTIALS_MESSAGE;
  }
}

export interface ExternalSignUpCallbackResult {
  success: boolean;
  redirectURL: string;
  errorMessage: string;
}
interface PKCERequest {
  codeVerifier: string;
  flow: string;
  redirectUrl: string;
  scope: string;
  clientId: string;
  baseURL: string;
}

interface PKETokenResult {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
  expires_on: number;
  resource: string;
  id_token_expires_in: number;
  profile_info: string;
  scope: string;
  refresh_token: string;
  refresh_token_expires_in: number;
}

export interface loginOtpResult {
  success: boolean;
  actionType: ActionType;
  errorMessage: string;
}
